<template>
  <div class="clubs-list max-w-4xl mx-auto px-4">
    <!-- 載入器 -->
    <div v-if="isLoading" class="flex justify-center items-center h-48">
      <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
    </div>

    <!-- 武場列表 -->
    <div v-else>
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-wood-800 text-center mb-2">武場列表</h1>
        <p class="text-wood-600 text-center">探索各地太極拳武場，加入練習社群</p>
      </div>

      <!-- 武場卡片網格 -->
      <div v-if="clubs.length" class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div 
          v-for="club in clubs" 
          :key="club.id"
          class="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden post-shadow hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          <!-- 武場圖片 -->
          <div class="relative h-48 overflow-hidden">
            <img 
              :src="club.attachment || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Yang_cheng_fu_single_whip_application_2_75.jpg/280px-Yang_cheng_fu_single_whip_application_2_75.jpg'"
              :alt="club.name"
              class="w-full h-full object-cover"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
          </div>

          <!-- 武場資訊 -->
          <div class="p-6">
            <h3 class="text-xl font-bold text-wood-800 mb-2 text-center">
              <a 
                :href="`/clubs/${club.id}`" 
                @click="navigateToClub($event, club.id)"
                class="hover:text-wood-600 transition-colors duration-200"
              >
                {{ club.name }}
              </a>
            </h3>
            
            <p class="text-wood-700 mb-4 leading-relaxed">
              {{ club.note }}
            </p>

            <!-- 位置資訊 -->
            <div class="border-t border-wood-200 pt-4 mb-4">
              <div class="flex items-center text-wood-600">
                <i class="fas fa-map-marker-alt mr-2"></i>
                <span>{{ club.position }}</span>
              </div>
            </div>

            <!-- 加入按鈕 -->
            <div class="text-center">
              <template v-if="userStore.isLoggedIn">
                <button
                  v-if="isMember(club.id)"
                  @click="leaveClub(club)"
                  class="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  退出武場
                </button>
                <button
                  v-else
                  @click="joinClub(club)"
                  class="px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  申請加入
                </button>
              </template>
              <template v-else>
                <button 
                  class="px-6 py-2 bg-wood-300 text-wood-800 rounded-xl cursor-not-allowed"
                  disabled
                >
                  登入才可加入武場
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- 空狀態 -->
      <div v-else class="text-center py-12">
        <div class="w-24 h-24 mx-auto mb-4 rounded-full yin-yang opacity-50"></div>
        <h3 class="text-xl font-medium text-wood-700 mb-2">目前沒有武場</h3>
        <p class="text-wood-500">請稍後再回來查看</p>
      </div>

      <!-- 操作按鈕 -->
      <div class="flex justify-center space-x-4 mt-8">
        <button
          v-if="userStore.isLoggedIn"
          @click="createNewClub"
          class="px-6 py-3 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
        >
          <i class="fas fa-plus mr-2"></i>
          創建新武場
        </button>
        <button
          @click="goHome"
          class="px-6 py-3 bg-wood-200 hover:bg-wood-300 text-wood-800 rounded-xl transition-all duration-300"
        >
          <i class="fas fa-home mr-2"></i>
          返回首頁
        </button>
      </div>
    </div>

    <!-- 創建武場表單 Modal -->
    <ClubEdit 
      v-if="showCreateForm"
      @close="closeCreateForm"
      @club-created="handleClubCreated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useUserStore } from '../stores/user.js'
import { useClubsStore } from '../stores/clubs.js'
import ClubEdit from './ClubEdit.vue'

// 使用 Pinia stores
const userStore = useUserStore()
const clubsStore = useClubsStore()

// 組件狀態
const isLoading = ref(true)
const clubs = ref([])
const userClubs = ref([])
const showCreateForm = ref(false)

// 檢查使用者是否為武場成員
const isMember = (clubId) => {
  return userClubs.value.some(club => club.id === clubId)
}

// 加入武場
const joinClub = async (club) => {
  try {
    // 直接調用 store 方法，它會處理頁面導向
    await clubsStore.joinClub(club.id)
  } catch (error) {
    console.error('加入武場失敗:', error)
  }
}

// 離開武場
const leaveClub = async (club) => {
  if (confirm(`您確定要退出「${club.name}」武場嗎？您一旦退出，可能需要重新申請加入。`)) {
    try {
      // 直接調用 store 方法，它會處理表單提交
      await clubsStore.leaveClub(club.id)
    } catch (error) {
      console.error('退出武場失敗:', error)
    }
  }
}

// 創建新武場
const createNewClub = () => {
  showCreateForm.value = true
}

// 關閉創建表單
const closeCreateForm = () => {
  showCreateForm.value = false
}

// 處理武場創建成功
const handleClubCreated = (newClub) => {
  console.log('新武場創建成功:', newClub)
  // 重新載入武場列表
  loadClubs()
  showCreateForm.value = false
}

// 導航到武場詳情
const navigateToClub = (event, clubId) => {
  event.preventDefault()
  console.log('點擊武場詳情，ID:', clubId)
  
  // 更新 URL
  const url = `/clubs/${clubId}`
  window.history.pushState(null, '', url)
  
  // 觸發路由變更事件
  window.dispatchEvent(new Event('popstate'))
}

// 返回首頁
const goHome = () => {
  // 觸發父組件的首頁導航
  window.history.pushState(null, '', '/')
  window.dispatchEvent(new Event('popstate'))
}

// 載入使用者加入的武場列表
const loadUserClubs = async () => {
  if (userStore.isLoggedIn) {
    try {
      userClubs.value = await userStore.getUserClubs()
    } catch (error) {
      console.error('載入使用者武場失敗:', error)
      userClubs.value = []
    }
  }
}

// 載入武場列表
const loadClubs = async () => {
  try {
    isLoading.value = true
    clubs.value = await clubsStore.getClubs()
    await loadUserClubs()
  } catch (error) {
    console.error('載入武場列表失敗:', error)
    clubs.value = []
  } finally {
    isLoading.value = false
  }
}

// 組件掛載時載入資料
onMounted(async () => {
  await loadClubs()
})
</script>

<style scoped>
/* 使用與 App.vue 相同的樣式系統 */
.clubs-list {
  @apply min-h-screen;
}

/* 武場卡片特殊效果 */
.club-card:hover {
  transform: translateY(-4px);
}

/* 繼承 App.vue 的樣式 */
.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

.post-shadow {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.animate-pulse-soft {
  animation: pulseSoft 2s ease-in-out infinite;
}

/* 木質色彩系統 - 需要在 scoped 內重新定義 */
.bg-wood-300 { background-color: #dcc89a; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
.bg-wood-800 { background-color: #4a3f35; }

.text-wood-600 { color: #8b7355; }
.text-wood-700 { color: #6d5a47; }
.text-wood-800 { color: #4a3f35; }

.hover\:bg-wood-300:hover { background-color: #dcc89a; }
.hover\:from-wood-600:hover { --tw-gradient-from: #8b7355; }
.hover\:to-wood-700:hover { --tw-gradient-to: #6d5a47; }

/* 漸變背景效果 */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-wood-500 {
  --tw-gradient-from: #a68b5b;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(166, 139, 91, 0));
}

.to-wood-600 {
  --tw-gradient-to: #8b7355;
}

.from-red-500 {
  --tw-gradient-from: #ef4444;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(239, 68, 68, 0));
}

.to-red-600 {
  --tw-gradient-to: #dc2626;
}
</style>